import { Avatar, Image } from "antd";
import { AiOutlineUser } from "react-icons/ai";
import { useEffect, useRef, useState } from "react";
import { useMyContext } from "../../../../context/MyContext";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../../../config/api";
import Loader from "../../../shared/loader/loader";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { storage } from "../../../../config/firebase";
import { IoClose } from "react-icons/io5";
import { getUser } from "../../../../../src/lib/user";
import pdfIcon from "../../../../assets/pdf.png";
import excelIcon from "../../../../assets/excel.png";
import fileIcon from "../../../../assets/file.svg";
import { useCallback } from "react";
import { useMemo } from "react";
import { Download, FileText, Loader2, MessageSquareIcon, Paperclip, Send, X, Check } from "lucide-react";
import { toast } from "react-toastify";

const Chat = ({ ticket }) => {
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [senderInfo, setSenderInfo] = useState({});
  const [userNames, setUserNames] = useState({});
  const [userNamesLoading, setUserNamesLoading] = useState(true);
  const [localMessages, setLocalMessages] = useState([]);
  const [needsFallbackAdmin, setNeedsFallbackAdmin] = useState(false);
  const [loading, setLoading] = useState(false)
  const { user, token, onlineUsers, socket } = useMyContext();

  const chatBoxRef = useRef(null);
  const socketEventAttached = useRef(false);
  const ticketListenerRef = useRef(null);
  const messagesUpdatedListenerRef = useRef(null);

  useEffect(() => {
    // Helper to (re)join the active ticket room on connect/reconnect
    const joinRoom = () => {
      if (ticket?._id) {
        socket.emit("join-ticket", ticket._id);
      }
    };

    // Join immediately on mount/change
    joinRoom();

    // Re-join after reconnects to ensure room membership is restored
    socket.on('connect', joinRoom);
    socket.on('reconnect', joinRoom);

    return () => {
      if (ticket?._id) {
        socket.emit("leave-ticket", ticket._id);
      }
      socket.off('connect', joinRoom);
      socket.off('reconnect', joinRoom);
      // Do NOT blanket-off event names here; specific cleanup happens below
    };
  }, [ticket?._id, socket]);

  // const handleInputChange = (e) => {
  //   const textarea = e.target
  //   setMessage(e.target.value);

  //   textarea.style.height = 'auto';
  //   textarea.style.height = `${textarea.scrollHeight}px`;
  // }

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    setFile(selectedFile);
    if (selectedFile) {
      const fileType = selectedFile.type;
      if (fileType.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = () => {
          setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        // For non-image files, set a generic preview
        setPreviewUrl({
          icon: getFileIcon(fileType),
          name: selectedFile.name,
        });
      }
    }
  };

  // Renders two identical checks with precise overlap and alignment
  const DoubleCheck = ({ size = 14, colorClass = "text-gray-400" }) => {
    const offset = Math.round(size * 0.45); // horizontal shift for the second tick
    return (
      <span
        className="relative inline-block align-middle"
        style={{ width: size + offset, height: size }}
      >
        <Check size={size} className={`${colorClass}`} style={{ position: 'absolute', top: 0, left: 0 }} />
        <Check size={size} className={`${colorClass}`} style={{ position: 'absolute', top: 0, left: offset }} />
      </span>
    );
  };

  const getFileIconFromUrl = (url) => {
    if (url.endsWith('.pdf')) return pdfIcon;
    if (url.endsWith('.xlsx') || url.endsWith('.xls') || url.endsWith('.csv')) return excelIcon;
    return fileIcon;
  };

  const getFileNameFromUrl = (url) => {
    return url.split('/').pop();
  };

  const getAttachmentMeta = useCallback((msg) => {
    const url = msg?.fileUrl || null;
    if (!url) return null;
    const fileName = msg?.fileName || decodeURIComponent(getFileNameFromUrl(url) || "attachment");
    const extension = fileName.split(".").pop()?.toLowerCase() || "file";
    return { url, fileName, extension };
  }, []);

  const getAttachmentTone = useCallback((extension) => {
    if (extension === "pdf") return "bg-red-100 text-red-700";
    if (extension === "xls" || extension === "xlsx") return "bg-green-100 text-green-700";
    if (extension === "csv") return "bg-emerald-100 text-emerald-700";
    return "bg-slate-100 text-slate-700";
  }, []);

  const getFileIcon = (fileType) => {
    if (fileType === "application/pdf") return pdfIcon;
    if (
      fileType ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      fileType === "application/vnd.ms-excel"
    )
      return excelIcon;
    if (fileType === "text/csv") return excelIcon;
    return fileIcon;
  };

  const getFallbackAdmin = useCallback(() => {
    console.log('---------- FALLBACK ADMIN FUNCTION CALLED ---------------');

    const adminRoles = ['admin', 'super admin', 'managing admin', 'lead admin', 'editor'];

    // Find the first online admin from onlineUsers
    const onlineAdmin = onlineUsers.find((user) => adminRoles.includes(user.role) && user.isOnline);

    console.log('~~~~~~~~~ ONLINE USERS FROM FALLBACK FUNCTION', onlineUsers);
    console.log('~~~~~~~~~ ONLINE ADMIN FOUND', onlineAdmin);

    if (onlineAdmin) {
      console.log('---------- FALLBACK ADMIN SET ---------------');
      setSenderInfo(onlineAdmin);
      setNeedsFallbackAdmin(false); // Reset the flag once we've found an admin
    }
  }, [onlineUsers]);

  const { data, isLoading, refetch } = useQuery(
    ["messages", ticket?._id],
    async () => {
      if (!token) {
        throw new Error("No authentication token available");
      }
      const res = await api.get(`/tickets/chats/${ticket?._id}/${user?.role === 'user' ? 'user' : 'admin'}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!ticket?._id && !!user?.role && !!token,
      onSuccess: (data) => {
        const msg = data?.messages;
        const sender = data?.sender;

        setLocalMessages(msg);
        if (sender) {
          setSenderInfo(sender);
          setNeedsFallbackAdmin(false);
        } else {
          // Mark that we need a fallback admin instead of calling it directly
          setNeedsFallbackAdmin(true);
        }
      },
      onError: (error) => {
        console.error("Error fetching messages:", error);
      },
    }
  );

  // NEW: Separate effect to handle fallback admin when onlineUsers is ready
  useEffect(() => {
    if (needsFallbackAdmin && onlineUsers && onlineUsers.length > 0) {
      console.log('Attempting to get fallback admin now that onlineUsers is populated');
      getFallbackAdmin();
    }
  }, [needsFallbackAdmin, onlineUsers, getFallbackAdmin]);

  // Set up socket event listener ONCE after initial data is loaded
  useEffect(() => {
    if (data && !socketEventAttached.current) {
      const onTicket = (newMessage) => {
        if (newMessage.ticketId === ticket?._id) {
          setLocalMessages((prevMessages) => {
            // 1) Reconcile by clientId if present
            if (newMessage.clientId) {
              const idx = prevMessages.findIndex(m => m.clientId && m.clientId === newMessage.clientId);
              if (idx !== -1) {
                const copy = [...prevMessages];
                copy[idx] = { ...copy[idx], ...newMessage };
                return copy;
              }
            }
            // 2) Dedupe by id or by content/time heuristic
            const isDup = prevMessages.some(m =>
              (m._id && newMessage._id && m._id === newMessage._id) ||
              (
                m.sender === newMessage.sender &&
                m.content === newMessage.content &&
                Math.abs(new Date(m.date) - new Date(newMessage.date)) < 5000
              )
            );
            if (!isDup) {
              if (newMessage?.sender !== user?.uid && newMessage?._id) {
                socket.emit('message-delivered', { messageIds: [newMessage._id], ticketId: ticket?._id, ackBy: user?.uid });
              }
              return [...prevMessages, newMessage];
            }
            // 3) Merge best-effort if duplicate appears without id then arrives with id
            const idxNoId = prevMessages.findIndex(m => !m._id && m.sender === newMessage.sender && m.content === newMessage.content);
            if (idxNoId !== -1) {
              const copy = [...prevMessages];
              copy[idxNoId] = { ...copy[idxNoId], ...newMessage };
              return copy;
            }
            return prevMessages;
          });
        }
      };

      // Listen for status updates (delivered/read)
      const onUpdated = (updatedMessages) => {
        if (!Array.isArray(updatedMessages) || updatedMessages.length === 0) return;
        setLocalMessages((prev) => {
          const byId = new Map(updatedMessages.filter(m => m._id).map(m => [m._id, m]));
          const byClient = new Map(updatedMessages.filter(m => m.clientId).map(m => [m.clientId, m]));
          return prev.map((m) => {
            if (m._id && byId.has(m._id)) return { ...m, ...byId.get(m._id) };
            if (m.clientId && byClient.has(m.clientId)) return { ...m, ...byClient.get(m.clientId) };
            return m;
          });
        });
      };

      ticketListenerRef.current = onTicket;
      messagesUpdatedListenerRef.current = onUpdated;

      socket.on("ticket-listner", onTicket);
      socket.on("messages-updated", onUpdated);

      socketEventAttached.current = true;
    }
  }, [data, ticket?._id, socket, user?.uid]);

  // Cleanup listeners when ticket changes or component unmounts
  useEffect(() => {
    return () => {
      if (ticketListenerRef.current) {
        socket.off('ticket-listner', ticketListenerRef.current);
      }
      if (messagesUpdatedListenerRef.current) {
        socket.off('messages-updated', messagesUpdatedListenerRef.current);
      }
      socketEventAttached.current = false;
      ticketListenerRef.current = null;
      messagesUpdatedListenerRef.current = null;
    };
  }, [ticket?._id, socket]);

  // Scroll to bottom when messages change
  useEffect(() => {
    // Smoothly scroll only the chat container; do not affect page scroll
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTo({
        top: chatBoxRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [localMessages]);

  // Helper: mark unread incoming messages as read when visible/active
  const markUnreadAsRead = useCallback(() => {
    if (!ticket?._id || !localMessages?.length) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    const unreadIncoming = localMessages
      .filter(m => m?.sender !== user?.uid && (m?.status === 'sent' || m?.status === 'delivered' || (!m?.status && !m?.readAt)))
      .map(m => m?._id)
      .filter(Boolean);
    if (unreadIncoming.length > 0) {
      socket.emit('message-read', { messageIds: unreadIncoming, ticketId: ticket?._id, ackBy: user?.uid });
    }
  }, [localMessages, ticket?._id, socket, user?.uid]);

  // Mark visible incoming messages as read when chat is open/active
  useEffect(() => {
    markUnreadAsRead();
  }, [markUnreadAsRead]);

  // Also handle when the user returns to the tab/window
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        markUnreadAsRead();
      }
    };
    const onFocus = () => markUnreadAsRead();
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
    if (typeof window !== 'undefined') window.addEventListener('focus', onFocus);
    return () => {
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
      if (typeof window !== 'undefined') window.removeEventListener('focus', onFocus);
    };
  }, [markUnreadAsRead]);

  const sendNotification = async (newMessage) => {
    const optionsArray = [
      "super admin",
      "managing admin",
      "lead admin",
      "editor",
    ];
    try {
      if (!token) {
        throw new Error("No authentication token available");
      }
      await api.post(
        "/notifications",
        {
          userId: optionsArray.includes(user?.role) ? ticket?.userId : null,
          title: `Ticket #${ticket?.ticketNumber} New Message`,
          message: newMessage,
          linkText: "Chat",
          link: `/tickets/${ticket?._id}`,
          quoteNumber: ticket?.quoteNumber,
          orderNumber: ticket?.orderNumber,
          role: optionsArray.includes(user?.role) ? null : "admin",
          type: "ticket-chat",
          refId: ticket?._id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } catch (error) {
      console.log("Error", error);
    }
  };

  const isSubmitting = useRef(false);

  const handleSubmit = async () => {
    if ((!file && !message) || isSubmitting.current) return;

    isSubmitting.current = true;
    let fileUrl = null;
    let fileName = null;
    const clientId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    const hasAttachment = Boolean(file);
    if (hasAttachment) setLoading(true);
    try {
      if (file) {
        const safeName = `${Date.now()}_${file.name}`;
        const imageRef = ref(storage, `/ticket_chat_attachments/${safeName}`);
        await uploadBytes(imageRef, file);
        fileUrl = await getDownloadURL(imageRef);
        fileName = file.name;
      }

      const newMessage = {
        content: message,
        sender: user?.uid,
        receiver: user?.role === "admin" ? ticket?.userId : "admin",
        date: dayjs().toDate(),
        ticketId: ticket?._id,
        fileUrl,
        fileName,
        clientId,
        senderName: user?.name,
        senderRole: user?.role,
        status: 'sent',
      };

      setLocalMessages(prev => [...prev, newMessage]);
      socket.emit("ticket-chat", newMessage);

      setMessage("");
      setFile(null);
      setPreviewUrl("");

      // Fire-and-forget to avoid blocking the UI send path
      sendNotification(message || (fileName ? `Attachment: ${fileName}` : ""))
        .catch(() => {});
      // Rely on socket echo instead of forcing an immediate refetch
      // refetch can run in background if needed
      // void refetch();
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    } finally {
      if (hasAttachment) setLoading(false)
      setTimeout(() => {
        isSubmitting.current = false;
      }, 500);
    }
  };

  const downloadAttachment = useCallback(async (url, fileName) => {
    if (!url) {
      toast.error("Attachment URL is missing");
      return;
    }
    try {
      const response = await fetch(
        `/api/tracking/download-attachment?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(fileName || "attachment")}`
      );
      if (!response.ok) throw new Error("Failed to fetch attachment");

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName || "attachment";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (_error) {
      toast.error("Unable to download attachment. Please try again.");
    }
  }, []);

  const handleClosePreview = () => {
    setPreviewUrl("");
    setFile(null);
    setMessage("");
  };

  const isOnline = useMemo(() => {
    if (!onlineUsers?.length || !senderInfo) return false;

    return onlineUsers.some(
      (user) => (user.userId === senderInfo.id || user.userId === senderInfo.userId) && user.isOnline
    );
  }, [onlineUsers, senderInfo]);

  useEffect(() => {
    const fetchUserNames = async () => {
      setUserNamesLoading(true);
      const updatedUserNames = { ...userNames };
      for (const message of localMessages) {
        if (!updatedUserNames[message.sender]) {
          try {
            const userData = await getUser(message.sender);
            updatedUserNames[message.sender] = userData.name || "Unknown User";
          } catch (error) {
            console.error("Error fetching user name:", error);
            updatedUserNames[message.sender] = "Unknown User";
          }
        }
      }
      setUserNames(updatedUserNames);
      setUserNamesLoading(false);
    };

    fetchUserNames();
  }, [localMessages]);

  const renderMessageSender = (message) => {
    if (userNamesLoading) {
      return "Loading...";
    }
    if (message.sender === user.uid) {
      return user.name;
    } else if (user.role === "user") {
      return "Admin";
    } else {
      return userNames[message.sender] || message.sender;
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <div className="w-full h-full bg-white rounded-xl shadow-lg flex flex-col items-center justify-start overflow-hidden">
        <div className="flex items-center justify-between w-full bg-cus-blue py-4 px-6">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center relative rounded-full">
              <Avatar
                icon={<AiOutlineUser />}
                className="bg-white text-cus-blue"
                size={40}
                src={senderInfo?.profilePicture}
              />
              <div className={`absolute top-0 right-0  size-3 rounded-full ${isOnline ? "bg-green-500" : "bg-red-500"}`} />
            </div>
            <div className="flex flex-col items-start text-white">
              <p className="text-xl font-medium">{user?.role === 'user' ? "Admin" : senderInfo?.name}</p>
              <p className="text-xs">{isOnline ? "Online" : "Offline"}</p>
            </div>
          </div>
        </div>
        <div className="w-full relative h-full overflow-auto">
          {previewUrl ? (
            <div className="absolute bg-white z-50 inset-0 white flex flex-col items-center justify-between p-4">
              <button
                type="button"
                onClick={() => handleClosePreview()}
                className="absolute top-2 left-2 rounded p-1 hover:bg-gray-100"
              >
                <X />
              </button>
              <div className="w-full h-full flex items-center justify-center">
                {typeof previewUrl === "string" ? (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full max-w-4xl h-full object-contain"
                  />
                ) : (
                  <div className="flex flex-col items-center">
                    <img
                      src={previewUrl.icon}
                      alt="File icon"
                      className="w-24 h-24"
                    />
                    <p className="mt-2 text-sm text-gray-600">{previewUrl.name}</p>
                  </div>
                )}
              </div>
              <div className="w-full self-end flex items-center gap-2 p-2">
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSubmit();
                    }
                  }}
                  className="bg-gray-100 rounded-lg p-2 w-full"
                  placeholder="Add Caption" />
                <button
                  type="button"
                  disabled={loading}
                  className="btnBg text-white rounded-lg p-2 disabled:bg-opacity-50"
                  onClick={handleSubmit}
                >
                  {loading ? <Loader2 className="animate-spin" /> : <Send />}
                </button>
              </div>
            </div>
          ) : null}
          <div ref={chatBoxRef} className="grow w-full h-full overflow-y-auto gap-4 flex flex-col items-center justify-start p-4">
            {localMessages.length > 0 ? (
              localMessages.map((msg, idx) => {
                const isSender = msg?.sender === user?.uid;
                const senderName = renderMessageSender(msg);
                const attachment = getAttachmentMeta(msg);
                const derivedStatus = msg?.status || 'sent';
                return (
                  <div key={idx} className={`flex flex-col gap-1 ${isSender ? 'self-end items-end' : 'self-start items-start'}`}>
                    <p className=" font-light text-xs">{dayjs(msg?.date).format("DD MMM YYYY") ===
                      dayjs().format("DD MMM YYYY")
                      ? dayjs(msg?.date).format("hh:mm A")
                      : dayjs(msg?.date).format("DD/MM/YYYY hh:mm A")}
                    </p>
                    <div className={`flex gap-1 ${isSender ? 'flex-row-reverse' : 'flex-row'}`}>

                      <Avatar
                        icon={<AiOutlineUser />}
                        size={30}
                        src={isSender ? user?.profilePicture : senderInfo?.profilePicture}
                      />
                      <div className={`flex flex-col min-w-[150px] items-start justify-center px-2 py-1 rounded-xl shadow relative ${isSender ? 'bg-cus-blue text-white rounded-tr-none' : 'bg-gray-100 rounded-tl-none'}`}>
                        <p className="self-start font-medium text-xs">{senderName}</p>
                        {msg?.content ? <p>{msg?.content}</p> : null}
                        {attachment ? (
                          <button
                            type="button"
                            onClick={() => downloadAttachment(attachment.url, attachment.fileName)}
                            className={`mt-2 w-full inline-flex items-center justify-between gap-2 text-xs rounded-lg px-2 py-1.5 border transition-all ${
                              isSender
                                ? "bg-white/15 border-white/20 text-white hover:bg-white/25"
                                : "bg-white border-gray-300 text-cus-blue hover:bg-gray-50"
                            }`}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="size-6 rounded-md bg-black/10 flex items-center justify-center shrink-0">
                                <FileText size={13} />
                              </div>
                              <span className="max-w-[180px] truncate">{attachment.fileName}</span>
                              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${getAttachmentTone(attachment.extension)}`}>
                                {(attachment.extension || "FILE").toUpperCase().slice(0, 4)}
                              </span>
                            </div>
                            <div className="size-6 rounded-full bg-black/10 flex items-center justify-center shrink-0">
                              <Download size={12} />
                            </div>
                          </button>
                        ) : null}
                        {isSender ? (
                          <div className="mt-1 self-end flex items-center gap-1 text-[10px] opacity-80">
                            {derivedStatus === 'read' ? (
                              <DoubleCheck size={14} colorClass="text-sky-300" />
                            ) : derivedStatus === 'delivered' ? (
                              <DoubleCheck size={14} colorClass="text-gray-400" />
                            ) : (
                              <Check size={14} className={isSender ? 'text-white/80' : 'text-gray-400'} />
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="!self-center w-full h-full flex flex-col items-center justify-center gap-2">
                <div className="rounded-full aspect-square p-4 flex items-center bg-cus-blue/10 text-cus-blue">
                  <MessageSquareIcon size={48} />
                </div>
                <p className="text-xl font-medium">No Messages Yet</p>
                <p className="font-light">
                  {user?.role === 'admin' ? 'Start a conversation with the user regarding the ticket' : 'Start a conversation with the admin regarding the ticket'}
                </p>
              </div>
            )}
          </div>
        </div>
        {!previewUrl && (
          <div className="bg-white w-full flex flex-row items-end gap-2 border-t py-4 px-6">
            {/* <button type="button" className="border rounded-lg p-2">
          </button> */}
            <label
              htmlFor="uploadImage"
              className="border rounded-lg p-2 cursor-pointer"
            >
              <Paperclip />
              <input
                type="file"
                name="uploadImage"
                id="uploadImage"
                className="hidden"
                onChange={handleFileChange}
                accept=".png,.jpg,.jpeg,.pdf,.xlsx,.xls,.csv"
              />
            </label>
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type a message"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSubmit();
                }
              }}
              className="w-full grow border rounded-lg p-2 focus:outline-cus-blue overflow-auto"
            />
            <button disabled={loading} onClick={handleSubmit} type="button" className="btnBg text-white rounded-lg p-2 disabled:bg-opacity-50">
              {loading ? <Loader2 className="animate-spin" /> : <Send />}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default Chat;